import {
  QueryClient,
  dehydrate,
  useInfiniteQuery,
} from "@tanstack/react-query";
import type { NextPage, NextPageContext } from "next";
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import { LoadingOverlay } from "../../components/progress/LoadingOverlay";
import useAnalytics from "../../hooks/analytics";
import { useAuthState } from "../../hooks/auth";
import { fetchAllCreators } from "../../lib/backend";
import { ERROR_PAGE } from "../../lib/constants";

const AllCreatorsPageLayout = dynamic(() =>
  import("../../layouts/creator/allCreators").then(
    (mod) => mod.AllCreatorsPageLayout
  )
);

const AllCreatorsPage: NextPage = () => {
  const analytics = useAnalytics();
  const auth = useAuthState();
  const [isTracked, setIsTracked] = useState<boolean>(false);
  const creatorsQuery = useInfiniteQuery({
    queryKey: ["creatorsPage"],
    queryFn: ({ pageParam = "" }) => fetchAllCreators({ lastSlug: pageParam }),
    getNextPageParam: (lastPage) => {
      return lastPage[lastPage.length - 1]?.slug;
    },
  });
  const creators = creatorsQuery?.data;

  useEffect(() => {
    if (!isTracked && creators && analytics.isReady && !auth.loading) {
      analytics.trackEvent("viewed_all_artists_page", {
        authenticated: Boolean(auth.user),
      });
      setIsTracked(true);
    }
  }, [analytics, auth.loading, creators]);

  if (creatorsQuery.isLoading) {
    return <LoadingOverlay />;
  }

  return <AllCreatorsPageLayout creatorsQuery={creatorsQuery} />;
};

AllCreatorsPage.getInitialProps = async (ctx: NextPageContext) => {
  if (ctx.res) {
    try {
      const queryClient = new QueryClient();
      const data = await fetchAllCreators({});
      await queryClient.setQueryData(["creators"], data);
      return {
        dehydratedState: dehydrate(queryClient),
      };
    } catch (err) {
      ctx.res.writeHead(307, { Location: ERROR_PAGE });
      ctx.res.end();
    }
  }
  return {};
};

export default AllCreatorsPage;
